/**
 * File Serialization Utility for Atlas Forms
 *
 * Converts File objects in form data to base64-encoded JSON-serializable format.
 * This allows files to be stored in NVARCHAR(MAX) database columns without loss of data.
 *
 * Usage:
 * ```typescript
 * const result = await serializeFormData(formData);
 * if (result.success) {
 *   const json = JSON.stringify(result.data);
 *   // Send to backend
 * }
 * ```
 */

/**
 * Represents a serialized file object.
 * Contains all metadata needed to reconstruct the file.
 */
export interface SerializedFile {
  /** Original file name with extension */
  name: string;

  /** File size in bytes */
  size: number;

  /** MIME type of the file */
  type: string;

  /** Base64-encoded file content */
  data: string;

  /** Optional: last modified timestamp */
  lastModified?: number;
}

/**
 * Result of form data serialization.
 * Contains the serialized data and any errors encountered.
 */
export interface FileSerializationResult {
  /** Whether serialization succeeded (all files processed without errors) */
  success: boolean;

  /** Serialized form data ready for JSON.stringify() */
  data: Record<string, unknown>;

  /** Number of files found and serialized */
  fileCount: number;

  /** Total size of all files in bytes */
  totalSize: number;

  /** List of errors encountered during serialization */
  errors: Array<{ field: string; error: string }>;
}

/**
 * File upload validation limits
 */
export const FILE_VALIDATION_LIMITS = {
  MAX_FILE_SIZE: 500 * 1024 * 1024,        // 500MB per file
  MAX_FILES: 100,                           // Max 100 files
  MAX_TOTAL_SIZE: 5 * 1024 * 1024 * 1024,  // 5GB per submission
};

/**
 * Validation error result
 */
export interface ValidationError {
  valid: boolean;
  error?: string;
  fileIndex?: number;
}

/**
 * Logger interface for controlling console output
 */
export interface Logger {
  debug(message: string, data?: unknown): void;
  error(message: string, error?: unknown): void;
  warn(message: string, data?: unknown): void;
  isDebugEnabled(): boolean;
}

/**
 * Validate file array against size limits
 */
export function validateFiles(
  files: File[],
  limits: typeof FILE_VALIDATION_LIMITS = FILE_VALIDATION_LIMITS
): ValidationError {
  if (!files || files.length === 0) {
    return { valid: true };
  }

  if (files.length > limits.MAX_FILES) {
    return {
      valid: false,
      error: `Maximum ${limits.MAX_FILES} files allowed, got ${files.length}`
    };
  }

  let totalSize = 0;
  for (let i = 0; i < files.length; i++) {
    const file = files[i];

    if (file.size > limits.MAX_FILE_SIZE) {
      return {
        valid: false,
        fileIndex: i,
        error: `File "${file.name}" exceeds ${(limits.MAX_FILE_SIZE / (1024 * 1024)).toFixed(0)}MB limit (${(file.size / (1024 * 1024)).toFixed(2)}MB)`
      };
    }

    totalSize += file.size;
    if (totalSize > limits.MAX_TOTAL_SIZE) {
      return {
        valid: false,
        error: `Total upload size (${(totalSize / (1024 * 1024 * 1024)).toFixed(2)}GB) exceeds ${(limits.MAX_TOTAL_SIZE / (1024 * 1024 * 1024)).toFixed(0)}GB limit`
      };
    }
  }

  return { valid: true };
}

/**
 * Sanitize filename to prevent path traversal and XSS
 */
export function sanitizeFilename(filename: string): string {
  if (!filename || typeof filename !== 'string') return 'file';

  return filename
    .replace(/\.\./g, '')                    // Remove .. sequences
    .replace(/[\/\\]/g, '_')                 // Replace path separators
    .replace(/[<>:"|?*]/g, '_')              // Replace invalid chars
    .replace(/[\x00-\x1f\x7f]/g, '')        // Remove control characters
    .replace(/^\s+|\s+$/g, '')              // Trim whitespace
    .substring(0, 255);                      // Length limit
}

/**
 * Sanitize data for logging to protect PII
 */
export function sanitizeForLogging(data: unknown): unknown {
  if (typeof data === 'string') {
    // Mask common PII patterns
    return data
      .replace(/\d{3}-\d{2}-\d{4}/g, 'XXX-XX-XXXX')        // SSN
      .replace(/\d{16}/g, 'XXXX-XXXX-XXXX-XXXX')           // Credit card
      .replace(/[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}/g, 'user@email.com')  // Email
      .substring(0, 100);
  }

  if (Array.isArray(data)) {
    return data.map(item => {
      if (item && typeof item === 'object' && 'name' in item) {
        return {
          ...item,
          name: sanitizeFilename(item.name as string).substring(0, 50)
        };
      }
      return item;
    });
  }

  return data;
}

/**
 * Create a logger instance with dev/prod modes
 */
export function createLogger(isDevelopment: boolean = false): Logger {
  return {
    debug: (msg, data) => {
      if (isDevelopment) {
        console.log(msg, data ? sanitizeForLogging(data) : '');
      }
    },
    error: (msg, err) => {
      console.error(msg, err instanceof Error ? err.message : String(err));
    },
    warn: (msg, data) => {
      console.warn(msg, data ? sanitizeForLogging(data) : '');
    },
    isDebugEnabled: () => isDevelopment
  };
}

/**
 * Default logger instance (uses development mode based on environment)
 */
const defaultLogger = createLogger(typeof process !== 'undefined' && process.env?.NODE_ENV === 'development');

/**
 * Performance optimization constants
 */
export const PERFORMANCE_LIMITS = {
  MAX_CONCURRENT_FILES: 5,              // Max 5 files processing simultaneously
  FILE_PROCESSING_TIMEOUT: 60000,       // 60 second timeout per file
  CHUNK_SIZE: 8192,                     // 8KB chunks for base64 encoding
  MAX_MEMORY_WARNING_THRESHOLD: 100 * 1024 * 1024,  // Warn if >100MB memory used
};

/**
 * Process items with concurrency limit
 */
async function processBatch<T, R>(
  items: T[],
  processor: (item: T, index: number) => Promise<R>,
  concurrency: number = PERFORMANCE_LIMITS.MAX_CONCURRENT_FILES,
  logger: Logger = defaultLogger
): Promise<R[]> {
  const results: R[] = [];
  const executing: Promise<void>[] = [];

  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    const index = i;

    const promise = (async () => {
      try {
        if (logger.isDebugEnabled()) {
          logger.debug(`[file-serializer] Processing item [${index}/${items.length}]`);
        }
        const result = await processor(item, index);
        results[index] = result;
      } catch (error) {
        results[index] = error as any;
      }
    })();

    executing.push(promise);

    // Maintain concurrency limit
    if (executing.length >= concurrency) {
      await Promise.race(executing);
      executing.splice(executing.findIndex(p => p === promise), 1);
    }
  }

  // Wait for remaining promises
  await Promise.all(executing);
  return results;
}

/**
 * Wrap promise with timeout
 */
function withTimeout<T>(
  promise: Promise<T>,
  timeoutMs: number,
  timeoutError: string
): Promise<T> {
  return Promise.race([
    promise,
    new Promise<T>((_, reject) =>
      setTimeout(() => reject(new Error(timeoutError)), timeoutMs)
    )
  ]);
}

/**
 * Check memory usage and warn if too high
 */
function checkMemoryUsage(logger: Logger = defaultLogger): void {
  if (typeof window !== 'undefined' && performance && (performance as any).memory) {
    const memory = (performance as any).memory;
    const usedMemory = memory.usedJSHeapSize;

    if (usedMemory > PERFORMANCE_LIMITS.MAX_MEMORY_WARNING_THRESHOLD) {
      const memoryMB = (usedMemory / (1024 * 1024)).toFixed(2);
      logger.warn(
        `[file-serializer] High memory usage: ${memoryMB}MB. Consider processing fewer files or splitting submissions.`
      );
    }
  }
}

/**
 * Retry mechanism for transient failures
 */
async function withRetry<T>(
  fn: () => Promise<T>,
  maxRetries: number = 3,
  delayMs: number = 1000,
  logger: Logger = defaultLogger,
  label: string = 'operation'
): Promise<T> {
  let lastError: Error | null = null;

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      if (logger.isDebugEnabled() && attempt > 1) {
        logger.debug(`[file-serializer] Retry attempt ${attempt}/${maxRetries} for ${label}`);
      }
      return await fn();
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));

      if (attempt === maxRetries) {
        break;
      }

      // Exponential backoff: 1s, 2s, 4s
      const delay = delayMs * Math.pow(2, attempt - 1);
      if (logger.isDebugEnabled()) {
        logger.debug(`[file-serializer] ${label} failed (attempt ${attempt}/${maxRetries}), retrying in ${delay}ms...`);
      }

      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }

  throw lastError || new Error(`${label} failed after ${maxRetries} retries`);
}

/**
 * Serialize form data by converting File objects to base64-encoded objects.
 *
 * Recursively scans the data structure for File objects and converts them
 * to serializable format while preserving non-file data unchanged.
 *
 * Handles:
 * - Single files
 * - Arrays of files
 * - Nested objects containing files
 * - Mixed data types
 * - File validation (size limits, file count limits)
 *
 * @param data Form data potentially containing File objects
 * @param logger Optional logger instance (defaults to environment-based logger)
 * @param limits Optional validation limits (defaults to FILE_VALIDATION_LIMITS)
 * @returns Promise resolving to serialization result with serialized data
 *
 * @example
 * ```typescript
 * const formData = {
 *   name: 'John',
 *   document: File { name: 'resume.pdf', ... }
 * };
 * const result = await serializeFormData(formData);
 * // result.data = {
 * //   name: 'John',
 * //   document: {
 * //     name: 'resume.pdf',
 * //     size: 102400,
 * //     type: 'application/pdf',
 * //     data: 'JVBERi1...' // base64
 * //   }
 * // }
 * ```
 */
export async function serializeFormData(
  data: Record<string, unknown>,
  logger: Logger = defaultLogger,
  limits: typeof FILE_VALIDATION_LIMITS = FILE_VALIDATION_LIMITS,
  timeoutMs: number = 300000  // ← 5 minute timeout for entire serialization
): Promise<FileSerializationResult> {
  const errors: Array<{ field: string; error: string }> = [];
  const counters = { fileCount: 0, totalSize: 0 };

  try {
    // Step 1: Collect all files and validate
    const allFiles: File[] = [];
    for (const [, value] of Object.entries(data)) {
      if (value instanceof File) {
        allFiles.push(value);
      } else if (Array.isArray(value)) {
        allFiles.push(...value.filter(v => v instanceof File));
      }
    }

    // Validate files against limits
    const validation = validateFiles(allFiles, limits);
    if (!validation.valid) {
      const errorMsg = validation.error || 'File validation failed';
      logger.error('[file-serializer] 🚫 File validation failed:', errorMsg);
      return {
        success: false,
        data,
        fileCount: 0,
        totalSize: 0,
        errors: [{ field: 'files', error: errorMsg }]
      };
    }

    // Step 2: Log form data summary (sanitized for security)
    const formDataSummary: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(data)) {
      if (value instanceof File) {
        formDataSummary[key] = {
          type: 'File',
          name: sanitizeFilename((value as File).name),
          size: (value as File).size
        };
      } else if (Array.isArray(value) && value.length > 0 && value[0] instanceof File) {
        formDataSummary[key] = {
          type: 'File[]',
          count: value.length,
          files: (value as File[]).map(f => ({ name: sanitizeFilename(f.name), size: f.size }))
        };
      } else {
        formDataSummary[key] = value;
      }
    }

    if (logger.isDebugEnabled()) {
      logger.debug('[file-serializer] 🚀 serializeFormData called with:', formDataSummary);
    }

    // Step 3: Serialize files recursively (with timeout protection)
    const serialized = await withTimeout(
      serializeObjectRecursive(data, '', errors, counters, logger),
      timeoutMs,
      `Serialization timeout after ${timeoutMs}ms - operation took too long`
    );

    if (logger.isDebugEnabled()) {
      logger.debug('[file-serializer] ✅ serializeFormData COMPLETE:', {
        filesFound: counters.fileCount,
        totalSize: counters.totalSize,
        errors: errors.length,
        structureKeys: Object.keys(serialized)
      });
    }

    return {
      success: errors.length === 0,
      data: serialized,
      fileCount: counters.fileCount,
      totalSize: counters.totalSize,
      errors,
    };
  } catch (error) {
    logger.error('[file-serializer] ❌ serializeFormData error:', error);
    return {
      success: false,
      data,
      fileCount: 0,
      totalSize: 0,
      errors: [{ field: 'root', error: String(error) }],
    };
  }
}

/**
 * Deserialize a file object from serialized format back to File object.
 *
 * Decodes the base64 data and reconstructs a File object that can be
 * used to trigger downloads or preview files.
 *
 * @param serialized The serialized file object with base64 data
 * @returns File object that can be downloaded or previewed
 *
 * @throws Error if base64 decoding fails
 *
 * @example
 * ```typescript
 * const serialized = {
 *   name: 'document.pdf',
 *   size: 1024,
 *   type: 'application/pdf',
 *   data: 'JVBERi1...'
 * };
 * const file = deserializeFile(serialized);
 * // Can now use file.stream() or trigger download
 * ```
 */
export function deserializeFile(serialized: SerializedFile): File {
  const binaryString = atob(serialized.data);
  const bytes = new Uint8Array(binaryString.length);

  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }

  return new File([bytes], serialized.name, {
    type: serialized.type,
    lastModified: serialized.lastModified,
  });
}

/**
 * Check if a value is a serialized file object.
 *
 * Validates that the object has the required properties of a serialized file:
 * name, size, type, and data (base64).
 *
 * @param value Any value to check
 * @returns true if value is a SerializedFile object
 */
export function isSerializedFile(value: unknown): value is SerializedFile {
  if (typeof value !== 'object' || value === null) return false;

  const obj = value as Record<string, unknown>;
  return (
    typeof obj.name === 'string' &&
    typeof obj.size === 'number' &&
    typeof obj.type === 'string' &&
    typeof obj.data === 'string'
  );
}

/**
 * Check if form data contains any serialized files.
 *
 * Recursively scans the entire data structure for serialized file objects.
 * Useful for determining whether file processing is needed.
 *
 * @param data Form data to check
 * @returns true if any serialized files found
 */
export function containsSerializedFiles(
  data: Record<string, unknown>
): boolean {
  for (const value of Object.values(data)) {
    if (isSerializedFile(value)) return true;
    if (Array.isArray(value) && value.some(isSerializedFile)) return true;
    if (typeof value === 'object' && value !== null && !(value instanceof File)) {
      if (containsSerializedFiles(value as Record<string, unknown>)) return true;
    }
  }
  return false;
}

/**
 * Get total size of all serialized files in form data.
 *
 * Recursively sums the size property of all serialized file objects.
 * Useful for logging or validation.
 *
 * @param data Form data to measure
 * @returns Total size in bytes
 */
export function getTotalSerializedFileSize(data: Record<string, unknown>): number {
  let total = 0;

  for (const value of Object.values(data)) {
    if (isSerializedFile(value)) {
      total += value.size;
    } else if (Array.isArray(value)) {
      for (const item of value) {
        if (isSerializedFile(item)) {
          total += item.size;
        } else if (typeof item === 'object' && item !== null && !(item instanceof File)) {
          total += getTotalSerializedFileSize(item as Record<string, unknown>);
        }
      }
    } else if (typeof value === 'object' && value !== null && !(value instanceof File)) {
      total += getTotalSerializedFileSize(value as Record<string, unknown>);
    }
  }

  return total;
}

// ─────────────────────────────────────────────────────────────────
// Private Helpers
// ─────────────────────────────────────────────────────────────────

/**
 * Recursively serialize an object and its nested properties.
 *
 * @private
 */
async function serializeObjectRecursive(
  obj: Record<string, unknown>,
  path: string,
  errors: Array<{ field: string; error: string }>,
  counters: { fileCount: number; totalSize: number },
  logger: Logger = defaultLogger
): Promise<Record<string, unknown>> {
  const result: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(obj)) {
    const fieldPath = path ? `${path}.${key}` : key;
    console.log(`[file-serializer] Processing field "${fieldPath}":`, { type: typeof value, isFile: value instanceof File, value });

    if (value === null || value === undefined) {
      result[key] = value;
    } else if (value instanceof File) {
      if (logger.isDebugEnabled()) {
        logger.debug(`[file-serializer] ✅ Detected File at "${fieldPath}"`);
      }

      // Convert File to serializable format
      try {
        result[key] = await fileToSerializableObject(value as File, logger);
        counters.fileCount++;
        counters.totalSize += (value as File).size;
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error);
        logger.error(`[file-serializer] ❌ Failed to serialize file at "${fieldPath}":`, error);
        errors.push({ field: fieldPath, error: errorMessage });

        // ← RELIABILITY: Return error marker instead of null
        result[key] = {
          name: `[ERROR: ${(value as File).name}]`,
          size: 0,
          type: 'application/octet-stream',
          data: '',
          _error: errorMessage
        };
      }
    } else if (Array.isArray(value)) {
      // Handle arrays (could contain Files or nested objects)
      console.log(`[file-serializer] 📦 Detected Array at "${fieldPath}" with ${value.length} items`);
      const fileItemsCount = value.filter(item => item instanceof File).length;
      if (fileItemsCount > 0) {
        console.log(`[file-serializer] ✅ Array contains ${fileItemsCount} File(s):`);
        value.forEach((item, idx) => {
          if (item instanceof File) {
            console.log(`[file-serializer]   [${idx}] ${(item as File).name} (${(item as File).size} bytes)`);
          }
        });
      }

      // ← PERFORMANCE: Use batching with concurrency limit instead of Promise.all
      // This prevents memory exhaustion when processing many large files
      result[key] = await processBatch(
        value,
        async (item, index) => {
          if (item instanceof File) {
            try {
              if (logger.isDebugEnabled()) {
                logger.debug(`[file-serializer] 🔄 Serializing array item [${index}]: ${(item as File).name}`);
              }

              // ← PERFORMANCE: Add timeout to prevent hanging
              const serialized = await withTimeout(
                fileToSerializableObject(item as File, logger),
                PERFORMANCE_LIMITS.FILE_PROCESSING_TIMEOUT,
                `File processing timeout for ${(item as File).name}`
              );

              counters.fileCount++;
              counters.totalSize += item.size;

              if (logger.isDebugEnabled()) {
                logger.debug(`[file-serializer] ✅ Serialized array item [${index}]: ${serialized.name} → base64`);
              }

              // ← PERFORMANCE: Check memory after each file
              checkMemoryUsage(logger);

              return serialized;
            } catch (error) {
              const errorMessage = error instanceof Error ? error.message : String(error);
              logger.error(`[file-serializer] ❌ Failed to serialize array item [${index}]:`, error);
              errors.push({ field: `${fieldPath}[${index}]`, error: errorMessage });

              // ← RELIABILITY: Return error marker instead of null to prevent silent data loss
              return {
                name: `[ERROR: ${(item as File).name}]`,
                size: 0,
                type: 'application/octet-stream',
                data: '',
                _error: errorMessage
              } as any;
            }
          } else if (typeof item === 'object' && item !== null && !(item instanceof File)) {
            return await serializeObjectRecursive(
              item as Record<string, unknown>,
              `${fieldPath}[${index}]`,
              errors,
              counters,
              logger
            );
          }
          return item;
        },
        PERFORMANCE_LIMITS.MAX_CONCURRENT_FILES,
        logger
      );
    } else if (typeof value === 'object' && !(value instanceof File)) {
      // Recursively handle nested objects
      result[key] = await serializeObjectRecursive(
        value as Record<string, unknown>,
        fieldPath,
        errors,
        counters,
        logger
      );
    } else {
      // Primitive values pass through unchanged
      result[key] = value;
    }
  }

  return result;
}

/**
 * Convert a File object to serializable format with base64 encoding.
 * Uses optimized approach to avoid UI blocking for large files.
 * Includes retry logic for transient failures.
 *
 * @private
 */
async function fileToSerializableObject(file: File, logger: Logger = defaultLogger): Promise<SerializedFile> {
  // ← RELIABILITY: Validate file before processing
  if (!file || !(file instanceof File)) {
    throw new Error('Invalid file object');
  }

  if (file.size === 0) {
    logger.warn(`[file-serializer] ⚠️ File "${file.name}" is empty (0 bytes)`);
  }

  if (file.size > FILE_VALIDATION_LIMITS.MAX_FILE_SIZE) {
    throw new Error(`File "${file.name}" exceeds maximum size of ${FILE_VALIDATION_LIMITS.MAX_FILE_SIZE / (1024 * 1024)}MB`);
  }

  // ← RELIABILITY: Retry file reading for transient errors
  return withRetry(
    () => readFileWithArrayBuffer(file, logger),
    3,  // max retries
    500,  // initial delay
    logger,
    `reading file "${file.name}"`
  );
}

/**
 * Read file with ArrayBuffer and convert to base64
 * Extracted to allow retry logic
 *
 * @private
 */
function readFileWithArrayBuffer(file: File, logger: Logger = defaultLogger): Promise<SerializedFile> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    if (logger.isDebugEnabled()) {
      logger.debug(`[file-serializer] 📖 Starting FileReader for: ${file.name} (${file.size} bytes)`);
    }

    // ← SAFETY: Set timeout to prevent hanging FileReader
    const timeoutHandle = setTimeout(() => {
      reader.abort();
      reject(new Error(`FileReader timeout for file "${file.name}"`));
    }, PERFORMANCE_LIMITS.FILE_PROCESSING_TIMEOUT);

    reader.onload = () => {
      clearTimeout(timeoutHandle);

      try {
        // ← NULL SAFETY: Check reader.result is not null
        const result = reader.result;
        if (!result || !(result instanceof ArrayBuffer)) {
          throw new Error('FileReader.result is null or not an ArrayBuffer');
        }

        if (logger.isDebugEnabled()) {
          logger.debug(`[file-serializer] ✅ FileReader.onload: file="${file.name}", bytesRead=${result.byteLength}`);
        }

        // ← PERFORMANCE: Use optimized base64 encoding
        const uint8Array = new Uint8Array(result);
        const base64 = arrayBufferToBase64Optimized(uint8Array);

        if (logger.isDebugEnabled()) {
          logger.debug(`[file-serializer] ✅ Base64 encoding complete: length=${base64.length}`);
        }

        resolve({
          name: file.name,
          size: file.size,
          type: file.type,
          data: base64,
          lastModified: file.lastModified,
        });
      } catch (error) {
        logger.error(`[file-serializer] ❌ Error in FileReader.onload:`, error);
        reject(new Error(`Failed to encode file: ${String(error)}`));
      }
    };

    reader.onerror = () => {
      clearTimeout(timeoutHandle);
      const errorMessage = reader.error?.message ?? 'Unknown file read error';
      logger.error(`[file-serializer] ❌ FileReader.onerror:`, reader.error);
      reject(new Error(`Failed to read file "${file.name}": ${errorMessage}`));
    };

    reader.onabort = () => {
      clearTimeout(timeoutHandle);
      logger.warn(`[file-serializer] ⚠️ File read operation aborted for "${file.name}"`);
      reject(new Error(`File read operation aborted for "${file.name}"`));
    };

    reader.onprogress = (event) => {
      // ← SAFETY: Check event.loaded and event.total are valid
      if (event.lengthComputable && logger.isDebugEnabled()) {
        const percentComplete = Math.round((event.loaded / event.total) * 100);
        logger.debug(`[file-serializer] Progress: ${percentComplete}% for "${file.name}"`);
      }
    };

    // ← SAFETY: Check file can be read
    try {
      reader.readAsArrayBuffer(file);
    } catch (error) {
      clearTimeout(timeoutHandle);
      logger.error(`[file-serializer] ❌ Error starting FileReader for "${file.name}":`, error);
      reject(new Error(`Failed to start reading file "${file.name}": ${String(error)}`));
    }
  });
}

/**
 * Optimized ArrayBuffer to base64 conversion
 * Reduces memory usage and avoids UI blocking
 *
 * @private
 */
function arrayBufferToBase64Optimized(uint8Array: Uint8Array): string {
  const chunkSize = PERFORMANCE_LIMITS.CHUNK_SIZE;
  const chunks: string[] = [];

  // Process in chunks to improve performance and reduce memory
  for (let i = 0; i < uint8Array.length; i += chunkSize) {
    const chunk = uint8Array.subarray(i, i + chunkSize);
    // Use apply instead of spread to avoid stack overflow
    const chunkArray = Array.from(chunk);
    chunks.push(String.fromCharCode.apply(null, chunkArray as any));
  }

  const binaryString = chunks.join('');
  return btoa(binaryString);
}
